/**
 * 메뉴 즐겨찾기 — "이 기능을 화면에 둔다".
 *
 * 상단 드롭다운 메뉴의 왼쪽 아이콘이 토글이다. 액션 id 하나에 상태 하나이고, 화면에 놓이는
 * 자리(배치)는 두 종류다:
 *
 *   · 고정 배치  — 원래 화면에 버튼이 있는 기능. 즐겨찾기를 끄면 그 버튼을 **숨긴다**.
 *   · 액션 그룹  — 그 밖의 모든 기능. 즐겨찾기를 켜면 편집기 툴바에 버튼이 **붙는다**.
 *
 * 메뉴 항목 자체와 단축키는 즐겨찾기와 무관하게 늘 동작한다 — 버튼을 꺼도 기능을 잃지 않는다.
 *
 * 읽기·쓰기·이관은 이 파일 한 곳에서만 한다.
 */
import { loadSection, saveSection } from './appSettings';

/** 예전 핀 목록 키. 옛 빌드가 읽으므로 이관 뒤에도 함께 쓴다(스탠드얼론은 한 PC 에서 빌드가 갈린다). */
const LEGACY_KEY = 'livesql_pinnedActions';

/**
 * 원래 화면에 버튼이 있는 메뉴 액션 → 그 버튼.
 *
 * **핸들러가 같다고 여기에 넣지 않는다.** onExecutePlan·onNewTab 은 SQLEditor 로도 전달되지만
 * 거기서는 단축키(Ctrl+E, Alt+N)·우클릭 메뉴다 — 숨기면 단축키가 죽는다. 숨겨도 되는 버튼만
 * 여기 명시한다.
 *
 * 탐색기의 빈 화면 「연결 추가」 버튼은 넣지 않는다 — 연결이 하나도 없을 때의 유일한 입구다.
 */
export const FIXED_PLACEMENTS = {
  new: 'fileTabs.newTab',                    // 탭 바의 +
  'manage-connections': 'explorer.connect',  // 탐색기 머리말의 「연결」
  // 도구 창 — 편집기 툴바 오른편(EditorToolbarRight → AssistantToolButtons).
  // 사용·에러 로그는 메뉴 도움말 › 로 옮겨 툴바 버튼이 없다(2026-09-13) — 옛 저장본의 'tool-ops-log' 는 무시된다
  'tool-tuning-cases': 'toolbar.tuningCases',
  // 테이블 액세스 분석 메뉴는 인덱스·파티션 설계 창의 탭이 됐다(2026-10-15) — 툴바 버튼은 인덱스 설계를 연다. 옛 'tool-table-analysis' 는 무시된다
  'tool-index-design': 'toolbar.tableAnalysis',
  // 작업 탭 줄의 패널 단추(2026-10-19) — 「SQL 편집」 오른편 단추 줄에 즐겨찾기거나 켜져 있을 때만 선다.
  // 보기 › 의 켜고 끄는 두 항목은 ✓ 가 왼쪽 자리를 써서 즐겨찾기 표시를 오른쪽 끝에 둔다(Header)
  'view-analysis': 'toolbar.analysisMode',
  'view-assistant': 'toolbar.assistant',
  viewhistory: 'toolbar.sidePanel.history',
  challengejoin: 'toolbar.sidePanel.challenge',
};

export function isFixedAction(id) {
  return Object.prototype.hasOwnProperty.call(FIXED_PLACEMENTS, id);
}

/**
 * 액션 그룹(편집기 툴바에 붙인 액션 id 순서, 'separator' 포함).
 * 새 섹션에 없으면 옛 키를 한 번 읽어 옮기고, 그것도 없으면 기본값.
 */
export function loadActionGroup(defaults) {
  const fav = loadSection('favorites') || {};
  if (Array.isArray(fav.actionGroup)) return fav.actionGroup;
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) { /* 깨진 옛 값은 기본값으로 */ }
  return defaults;
}

/** 원래 화면에 있는 버튼 중 사용자가 끈 것. 기본은 빈 목록 — 업데이트했다고 버튼이 사라지면 안 된다. */
export function loadHiddenFixed() {
  const fav = loadSection('favorites') || {};
  return Array.isArray(fav.hiddenFixed) ? fav.hiddenFixed : [];
}

/**
 * 저장. 이 빌드가 모르는 id 도 그대로 둔다 — 새 빌드가 붙인 액션을 옛 빌드가 지우면
 * 되돌아갔을 때 설정이 사라진다(docs/schema-compat.md). 섹션의 모르는 키도 보존한다.
 */
export function saveFavorites(actionGroup, hiddenFixed) {
  const prev = loadSection('favorites') || {};
  saveSection('favorites', { ...prev, actionGroup, hiddenFixed });
  try { localStorage.setItem(LEGACY_KEY, JSON.stringify(actionGroup)); } catch (_) { /* 저장 불가 환경 */ }
}
